import React, { useState, useRef, useEffect } from "react";
import {
  Box, List, ListItem, ListItemText, Divider, Stack,
  Slider as MuiSlider, Typography, IconButton,
} from "@mui/material";
import { PlayArrow, Pause, Fullscreen, FullscreenExit } from "@mui/icons-material";
import { LifeBuoy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { HELP_VIDEOS } from "../../config/helpVideos";
import Loader from "../Loader";
import SetupGuideButton from "../firstRunGuide/SetupGuideButton";

const TutorialVideosPage = ({ videos = HELP_VIDEOS, defaultIndex = 0 }) => {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const navigate = useNavigate();

  const [selectedIndex, setSelectedIndex] = useState(defaultIndex);
  const [loading, setLoading]             = useState(true);
  const [isPlaying, setIsPlaying]         = useState(false);
  const [currentTime, setCurrentTime]     = useState(0);
  const [duration, setDuration]           = useState(0);
  const [videoAspect, setVideoAspect]     = useState(16 / 9);
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const videoRef     = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setVideoAspect(16 / 9);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [selectedIndex]);

  useEffect(() => {
    const handleFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration || 0);
      const { videoWidth, videoHeight } = videoRef.current;
      if (videoWidth && videoHeight) setVideoAspect(videoWidth / videoHeight);
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
  };

  const handleSeek = (_, val) => {
    if (videoRef.current) {
      videoRef.current.currentTime = val;
      setCurrentTime(val);
    }
  };

  const handleFullscreen = async () => {
    if (!document.fullscreenElement) await containerRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };

  const formatTime = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const currentVideo = videos[selectedIndex];
  const controlColor = dark ? "white" : "#525252";

  return (
    <div className="min-h-full p-0">
      <Box
        sx={{
          width: "100%",
          maxHeight: "90vh",
          bgcolor: dark ? "transparent" : "#ffffff",
          borderRadius: 2,
          border: `1px solid ${dark ? "rgba(90,68,121,0.64)" : "#d1d5db"}`,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* ── Left: video list ── */}
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            bgcolor: dark ? "transparent" : "#FAFAFA",
            borderRight: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)"}`,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}` }}>
            <Typography variant="h6" sx={{ color: dark ? "white" : "#525252", fontSize: "1rem" }}>
              Tutorial Videos
            </Typography>
            <Typography variant="body2" sx={{ color: dark ? "rgba(255,255,255,0.7)" : "#888", mt: 0.5, fontSize: "0.75rem" }}>
              Click on a video to watch it
            </Typography>
            {/* Restart / reopen the setup guide. Renders nothing once it is done. */}
            <div className="mt-3 empty:hidden">
              <SetupGuideButton />
            </div>
          </Box>

          <List disablePadding dense sx={{ flex: 1, overflowY: "auto" }}>
            {videos.map((video, index) => {
              const isActive = index === selectedIndex;
              return (
                <React.Fragment key={index}>
                  <ListItem
                    sx={{
                      cursor: "pointer",
                      bgcolor: isActive ? "rgba(79,70,229,0.1)" : "transparent",
                      borderLeft: isActive ? "4px solid #4f46e5" : "4px solid transparent",
                      py: 1,
                      "&:hover": { bgcolor: "rgba(79,70,229,0.08)" },
                    }}
                    onClick={() => setSelectedIndex(index)}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 22, height: 22, minWidth: 22,
                              borderRadius: "50%",
                              bgcolor: isActive ? "#4f46e5" : dark ? "rgba(255,255,255,0.1)" : "#525252",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: "white", fontWeight: "bold", fontSize: "0.7rem", flexShrink: 0,
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: isActive ? "bold" : "normal",
                              color: isActive ? "#4f46e5" : dark ? "white" : "#525252",
                              fontSize: "0.85rem",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {video.title}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider sx={{ bgcolor: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)" }} />
                </React.Fragment>
              );
            })}
          </List>

          {/* ── Didn't find what you needed? → Support (pinned to bottom) ── */}
          <Box
            sx={{
              mt: "auto",
              p: 1.5,
              borderTop: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
            }}
          >
            <Typography
              sx={{
                color: dark ? "rgba(255,255,255,0.7)" : "#888",
                fontSize: "0.75rem",
                mb: 1,
              }}
            >
              Didn&apos;t find what you needed?
            </Typography>
            <button
              type="button"
              onClick={() => navigate("/org-admin/raise-ticket")}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                width: "100%",
                padding: "8px 12px",
                borderRadius: 9999,
                border: "none",
                cursor: "pointer",
                color: "#ffffff",
                fontSize: "0.8rem",
                fontWeight: 500,
                background: "linear-gradient(to right, #ec4899, #3b82f6)",
              }}
            >
              Contact Support
            </button>
          </Box>
        </Box>

        {/* ── Right: video player ── */}
        <Box
          ref={containerRef}
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: dark ? "transparent" : "#FAFAFA",
          }}
        >
          <Box sx={{ position: "relative", width: "100%", aspectRatio: String(videoAspect) }}>
            {loading && currentVideo?.url && (
              <Box sx={{ position: "absolute", inset: 0, zIndex: 5, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: dark ? "transparent" : "#FAFAFA" }}>
                <Loader />
              </Box>
            )}

            {currentVideo?.url ? (
              <>
                <video
                  ref={videoRef}
                  key={currentVideo.url}
                  src={currentVideo.url}
                  style={{ width: "100%", height: "100%", display: "block" }}
                  onLoadedData={() => setLoading(false)}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime || 0)}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />
                {!loading && !isPlaying && (
                  <Box
                    onClick={handlePlayPause}
                    sx={{
                      position: "absolute", inset: 0, zIndex: 2,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", bgcolor: "rgba(0,0,0,0.25)",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64, height: 64, borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.92)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "scale(1.1)" },
                      }}
                    >
                      <PlayArrow sx={{ color: "#1a1a1a", fontSize: 38, ml: 0.5 }} />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: dark ? "rgba(255,255,255,0.4)" : "#aaa", fontSize: "0.9rem" }}>
                  No video available
                </Typography>
              </Box>
            )}
          </Box>

          {!loading && currentVideo?.url && (
            <Box
              sx={{
                px: 2, py: 1.5,
                bgcolor: dark ? "transparent" : "#FAFAFA",
                borderTop: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.08)"}`,
                display: "flex", flexDirection: "column", gap: 1,
              }}
            >
              <MuiSlider
                value={currentTime} min={0} max={duration || 0} step={0.1}
                onChange={handleSeek} size="small"
                sx={{
                  p: 0,
                  "& .MuiSlider-rail":  { bgcolor: dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)", height: 4 },
                  "& .MuiSlider-track": { background: "linear-gradient(to right, #ec4899, #3b82f6)", border: "none", height: 4 },
                  "& .MuiSlider-thumb": {
                    width: 12, height: 12, bgcolor: "#3b82f6",
                    "&:before": { display: "none" },
                    "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 8px rgba(59,130,246,0.16)" },
                  },
                }}
              />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <IconButton onClick={handlePlayPause} size="small" sx={{ color: controlColor }}>
                    {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                  </IconButton>
                  <Typography variant="body2" sx={{ color: controlColor, fontSize: "0.8rem", ml: 0.5 }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Typography>
                </Stack>
                <IconButton onClick={handleFullscreen} size="small" sx={{ color: controlColor }}>
                  {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                </IconButton>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default TutorialVideosPage;
